import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DetailPageLayout from '@/components/layout/DetailPageLayout';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommentSection from '@/components/feature/CommentSection';
import PortfolioPreviewModal from '@/components/common/PortfolioPreviewModal';
import { useAuth } from '@/hooks/useAuth';
import { fetchPublicJobPostingDetail, fetchPublicJobPostings } from '@/service/api/job';
import { applyJobPosting, fetchProviderJobResumeDraft } from '@/service/api/resume';
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';
import { uploadFile } from '@/utils/upload';
import { ApplyModal, DraftPromptModal, LoginPromptModal } from './modules';

/** 格式化日期为 YYYY-MM-DD */
function formatDateShort(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** 薪资周期映射 */
export const salaryPeriodMap: Record<string, string> = {
  MONTHLY: '月',
  YEARLY: '年',
  DAILY: '日',
  HOURLY: '小时',
  PROJECT: '项目',
};

/** 格式化薪资展示文本 */
export function formatJobSalaryDisplay(
  salaryMin?: string,
  salaryMax?: string,
  salaryType?: string,
  salaryPeriod?: string,
  salaryCurrency: string = 'CNY'
): string {
  const periodText = salaryPeriodMap[salaryPeriod || 'MONTHLY'] || salaryPeriod || '月';

  if (salaryType === 'NEGOTIABLE' || (!salaryMin && !salaryMax)) {
    return '面议';
  }

  const formatNum = (numStr?: string) => {
    if (!numStr) return '';
    const num = parseFloat(numStr);
    if (isNaN(num)) return numStr;
    if (num >= 10000) {
      return `${(num / 10000).toFixed(num % 10000 === 0 ? 0 : 1)}k`;
    }
    return `${num}`;
  };

  if (salaryType === 'FIXED' || (salaryMin && !salaryMax)) {
    return `${formatNum(salaryMin)}元/${periodText}`;
  }

  if (salaryMin && salaryMax) {
    return `${formatNum(salaryMin)} - ${formatNum(salaryMax)}元/${periodText}`;
  }

  return '面议';
}

/** 就业类型映射 */
export const employmentTypeMap: Record<string, string> = {
  FULL_TIME: '全职',
  PART_TIME: '兼职',
  INTERNSHIP: '实习',
  PROJECT: '项目外包',
};

/** 状态样式配置 */
const statusConfigMap: Record<string, { bg: string; color: string; label: string }> = {
  RECRUITING: { bg: 'bg-primary-50 border-primary-200', color: 'text-primary-600', label: '招聘中' },
  open: { bg: 'bg-primary-50 border-primary-200', color: 'text-primary-600', label: '招聘中' },
  PUBLISHED: { bg: 'bg-primary-50 border-primary-200', color: 'text-primary-600', label: '招聘中' },
  CLOSED: { bg: 'bg-gray-100 border-gray-200', color: 'text-foreground-500', label: '已关闭' },
  closed: { bg: 'bg-gray-100 border-gray-200', color: 'text-foreground-500', label: '已关闭' },
  COMPLETED: { bg: 'bg-emerald-50 border-emerald-200', color: 'text-emerald-600', label: '招聘完成' },
  completed: { bg: 'bg-emerald-50 border-emerald-200', color: 'text-emerald-600', label: '招聘完成' },
  DRAFT: { bg: 'bg-amber-50 border-amber-200', color: 'text-amber-600', label: '草稿' },
  PENDING_REVIEW: { bg: 'bg-blue-50 border-blue-200', color: 'text-blue-600', label: '审核中' },
};

export default function JobDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isLoggedIn, user, rawUser, isEnterprise } = useAuth();

  const [job, setJob] = useState<Api.Job.JobResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarJobs, setSimilarJobs] = useState<Api.Job.JobResponse[]>([]);

  // 申请/投递 Modal 状态
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showDraftPromptModal, setShowDraftPromptModal] = useState(false);
  const [jobResumeDraft, setJobResumeDraft] = useState<Api.Resume.ProviderJobResumeDraftResponse | null>(null);
  const [isCheckingDraft, setIsCheckingDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  const [applyError, setApplyError] = useState('');

  // 直投 (DIRECT_RESUME) / 提交模式控制状态
  const [submissionMode, setSubmissionMode] = useState<'PROVIDER_PROFILE' | 'DIRECT_RESUME'>('PROVIDER_PROFILE');
  const [directSalary, setDirectSalary] = useState('');
  const [directResumeAssetId, setDirectResumeAssetId] = useState('');
  const [directResumeFileName, setDirectResumeFileName] = useState('');
  const [isUploadingResume, setIsUploadingResume] = useState(false);

  // 媒体/图片预览 Modal 状态
  const [previewMedia, setPreviewMedia] = useState<{
    open: boolean;
    title?: string;
    intro?: string;
    isVideo?: boolean;
    mediaUrl?: string | null;
  }>({
    open: false,
    title: '',
    intro: '',
    isVideo: false,
    mediaUrl: null,
  });

  const handlePreviewImage = (img: Api.Job.DemandAttachmentResponse) => {
    if (!img.access_url) return;
    const isVid = (img.mime_type || '').startsWith('video/');
    setPreviewMedia({
      open: true,
      title: img.display_name || img.file_name || '',
      intro: undefined,
      isVideo: isVid,
      mediaUrl: img.access_url,
    });
  };

  const { categories, fetchCategories, getCategoryNameByCode } = useBusinessCategoryStore();

  const categoriesFetchedRef = useRef(false);
  const fetchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (categoriesFetchedRef.current || categories.length > 0) return;
    categoriesFetchedRef.current = true;
    fetchCategories().catch((err) => {
      console.error('获取分类失败:', err);
      categoriesFetchedRef.current = false;
    });
  }, [categories.length, fetchCategories]);

  useEffect(() => {
    if (!id) return;
    if (fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;

    setLoading(true);
    setError(null);

    fetchPublicJobPostingDetail(id)
      .then((res) => {
        setJob(res || null);
        // 获取同类推荐岗位
        const categoryCode = res?.category_codes?.[0];
        fetchPublicJobPostings({ category_code: categoryCode, page: 1, page_size: 4 })
          .then((listRes) => {
            const list = (listRes?.items || []).filter((item) => item.id !== id);
            setSimilarJobs(list.slice(0, 3));
          })
          .catch(() => { });
      })
      .catch((err) => {
        console.error('获取岗位详情失败:', err);
        setError('获取招聘岗位详情失败，该岗位可能已不存在或已被撤回');
        fetchedIdRef.current = null;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const handleApplyClick = async () => {
    if (isApplied) {
      navigate('/my-job-resume?tab=applications');
      return;
    }

    if (!isLoggedIn) {
      setShowLoginPrompt(true);
      return;
    }

    setIsCheckingDraft(true);
    setApplyError('');
    setApplySuccess(false);

    try {
      const draft = await fetchProviderJobResumeDraft();
      setJobResumeDraft(draft);

      const hasSalary = Boolean(draft?.expected_monthly_salary && draft.expected_monthly_salary.trim());
      const hasFile = Boolean(draft?.resume_file?.id || draft?.resume_file?.access_url || draft?.resume_file?.upload_asset_id);
      const hasCompleteResume = Boolean(draft && hasSalary && hasFile);

      if (hasCompleteResume) {
        // 有完整简历时默认选中【我的简历】Tab (PROVIDER_PROFILE)
        setSubmissionMode('PROVIDER_PROFILE');
        setDirectSalary(draft?.expected_monthly_salary || '');
        setDirectResumeAssetId(draft?.resume_file?.upload_asset_id || draft?.resume_file?.id || '');
        setDirectResumeFileName(draft?.resume_file?.file_name || draft?.resume_file?.display_name || '');
      } else {
        // 简历不完整时预填现有数据，默认选中【简历直投】Tab (DIRECT_RESUME)
        setSubmissionMode('DIRECT_RESUME');
        setDirectSalary(draft?.expected_monthly_salary || '');
        setDirectResumeAssetId(draft?.resume_file?.upload_asset_id || draft?.resume_file?.id || '');
        setDirectResumeFileName(draft?.resume_file?.file_name || draft?.resume_file?.display_name || '');
      }

      setShowApplyModal(true);
    } catch (err) {
      console.error('获取求职资料草稿失败:', err);
      setSubmissionMode('DIRECT_RESUME');
      setShowApplyModal(true);
    } finally {
      setIsCheckingDraft(false);
    }
  };

  // 简历文件上传处理
  const handleResumeFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingResume(true);
    setApplyError('');
    try {
      const uploadRes = await uploadFile(file, 'PROFILE');
      const assetId = uploadRes.upload_asset_id || uploadRes.asset_id;
      if (assetId) {
        setDirectResumeAssetId(String(assetId));
        setDirectResumeFileName(uploadRes.file_name || file.name);
      } else {
        setApplyError('简历上传失败，未取得资源 ID');
      }
    } catch (err: any) {
      console.error('简历上传失败:', err);
      setApplyError(err?.message || '简历上传失败，请重试');
    } finally {
      setIsUploadingResume(false);
    }
  };

  const handleConfirmSubmit = async () => {
    if (!id) return;

    if (submissionMode === 'DIRECT_RESUME') {
      const hasSalary = Boolean(directSalary && directSalary.trim());
      const hasFile = Boolean(directResumeAssetId);

      if (!hasSalary && !hasFile) {
        setApplyError('请填写期望月薪并上传简历文件后再进行提交');
        return;
      }
      if (!hasSalary) {
        setApplyError('请填写期望月薪后再进行提交');
        return;
      }
      if (!hasFile) {
        setApplyError('请上传简历文件后再进行提交');
        return;
      }
    } else if (submissionMode === 'PROVIDER_PROFILE') {
      const hasSalary = Boolean(jobResumeDraft?.expected_monthly_salary && jobResumeDraft.expected_monthly_salary.trim());
      const hasFile = Boolean(jobResumeDraft?.resume_file?.id || jobResumeDraft?.resume_file?.access_url || jobResumeDraft?.resume_file?.upload_asset_id);

      if (!hasSalary && !hasFile) {
        setApplyError('您的个人简历未填写期望月薪与上传简历附件，请先完善或选择“简历直投”');
        return;
      }
      if (!hasSalary) {
        setApplyError('您的个人简历未填写期望月薪，请先完善或选择“简历直投”');
        return;
      }
      if (!hasFile) {
        setApplyError('您的个人简历未上传简历附件，请先上传或选择“简历直投”');
        return;
      }
    }

    setIsSubmitting(true);
    setApplyError('');

    try {
      const payload: Api.Resume.ApplyJobRequest = {
        submission_mode: submissionMode,
        expected_monthly_salary: directSalary.trim() || undefined,
        resume_file_upload_asset_id: directResumeAssetId || undefined,
      };

      await applyJobPosting(id, payload);
      setApplySuccess(true);
      if (job) {
        setJob({ ...job, application_status: 'PENDING' });
      }
    } catch (err: any) {
      setApplyError(err?.message || '投递失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DetailPageLayout bgClass="bg-background-50">
        <div className="pt-20 sm:pt-24 pb-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm text-foreground-500">正在加载招聘岗位详情...</p>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  if (error || !job) {
    return (
      <DetailPageLayout bgClass="bg-background-50">
        <div className="pt-20 sm:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-950 mb-2">招聘岗位未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">{error || '该岗位可能已关闭或不存在'}</p>
            <Link to="/demands" className="text-sm text-primary-600 hover:text-primary-500 cursor-pointer">
              返回需求/招聘大厅
            </Link>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  const categoryNames =
    job.category_codes && job.category_codes.length > 0
      ? job.category_codes.map((code) => getCategoryNameByCode(code, code))
      : ['通用'];

  const statusKey = job.status || 'RECRUITING';
  const statusStyle = statusConfigMap[statusKey] || statusConfigMap['RECRUITING'];
  const salaryText = formatJobSalaryDisplay(
    job.salary_min,
    job.salary_max,
    job.salary_type,
    job.salary_period,
    job.salary_currency
  );

  const empTypeLabel = employmentTypeMap[job.employment_type || ''] || job.employment_type || '全职';
  const rawAppStatus = (job.application_status || '').toUpperCase();
  // 只有当申请状态为 PENDING (待审核/待处理) 时才显示为已投递，其他状态（如 WITHDRAWN 已撤回、REJECTED 已拒绝）允许再次投递
  const isApplied = applySuccess || rawAppStatus === 'PENDING';

  return (
    <DetailPageLayout
      bgClass="bg-background-50"
      header={
        <>
          {/* Breadcrumb */}
          <div className="pt-16 sm:pt-20">
            <div className="detail-shell">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '招聘大厅', href: '/demands' },
                  { label: job.title },
                ]}
              />
            </div>
          </div>

          {/* Job Main Header */}
          <section className="py-6 sm:py-8 bg-white border-b border-background-200">
            <div className="detail-shell">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="space-y-3 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${statusStyle.bg} ${statusStyle.color}`}>
                      {statusStyle.label}
                    </span>
                    {categoryNames.map((cat, idx) => (
                      <span key={idx} className="px-3 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-600">
                        {cat}
                      </span>
                    ))}
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-600">
                      {empTypeLabel}
                    </span>
                  </div>

                  <h1 className="text-2xl sm:text-3xl font-bold text-foreground-950 tracking-tight">
                    {job.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-foreground-500">
                    {job.enterprise_name_masked && (
                      <span className="flex items-center gap-1.5 font-medium text-foreground-800">
                        <i className="ri-building-4-line text-primary-500"></i>
                        {job.enterprise_name_masked}
                      </span>
                    )}
                    {job.city_name && (
                      <span className="flex items-center gap-1.5">
                        <i className="ri-map-pin-line text-foreground-400"></i>
                        {job.city_name}
                      </span>
                    )}
                    {job.published_at && (
                      <span className="flex items-center gap-1.5">
                        <i className="ri-time-line text-foreground-400"></i>
                        发布时间：{formatDateShort(job.published_at)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Salary & Action */}
                <div className="flex flex-col sm:flex-row lg:flex-col items-start lg:items-end justify-between gap-4 shrink-0">
                  <div className="text-left lg:text-right">
                    <span className="text-xs text-foreground-400 block mb-0.5">薪资待遇</span>
                    <span className="text-2xl sm:text-3xl font-bold text-red-500 font-heading">
                      {salaryText}
                    </span>
                  </div>

                  {!isEnterprise && (
                    <button
                      onClick={handleApplyClick}
                      disabled={statusKey !== 'RECRUITING'}
                      className={`w-full sm:w-auto px-8 py-3.5 rounded-2xl font-semibold text-sm inline-flex items-center justify-center gap-2 transition-all cursor-pointer shadow-sm ${isApplied
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white font-medium'
                        : statusKey !== 'RECRUITING'
                          ? 'bg-gray-200 text-foreground-400 cursor-not-allowed'
                          : 'bg-primary-500 text-white hover:brightness-95 active:brightness-90'
                        }`}
                      title={isApplied ? '已投递申请，点击查看投递记录' : undefined}
                    >
                      <i className={isApplied ? 'ri-checkbox-circle-line' : 'ri-send-plane-fill'}></i>
                      {isApplied
                        ? '已投递申请'
                        : statusKey === 'CLOSED'
                          ? '招聘已关闭'
                          : statusKey === 'COMPLETED'
                            ? '招聘已完成'
                            : '申请投递岗位'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      }
      sidebar={
        <>
          {/* Overview Box */}
          <div className="bg-white rounded-3xl p-6 border border-background-200/80 shadow-sm space-y-4">
            <h3 className="font-bold text-foreground-950 text-base border-b border-gray-100 pb-3">
              岗位概览
            </h3>

            <div className="space-y-3.5 text-sm">
              <div className="flex justify-between items-center text-foreground-600">
                <span className="text-foreground-400">工作类型</span>
                <span className="font-medium text-foreground-900">{empTypeLabel}</span>
              </div>
              <div className="flex justify-between items-center text-foreground-600">
                <span className="text-foreground-400">工作地点</span>
                <span className="font-medium text-foreground-900">{job.city_name || '未填'}</span>
              </div>
              <div className="flex justify-between items-center text-foreground-600">
                <span className="text-foreground-400">薪资周期</span>
                <span className="font-medium text-foreground-900">
                  {salaryPeriodMap[job.salary_period || ''] || job.salary_period || '月'}
                </span>
              </div>
              {job.deadline_at && (
                <div className="flex justify-between items-center text-foreground-600">
                  <span className="text-foreground-400">截止时间</span>
                  <span className="font-medium text-foreground-900 flex items-center gap-1">
                    {formatDateShort(job.deadline_at)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Recommendations */}
          {similarJobs.length > 0 && (
            <div className="bg-white rounded-3xl p-6 border border-background-200/80 shadow-sm space-y-4">
              <h3 className="font-bold text-foreground-950 text-base border-b border-gray-100 pb-3">
                推荐招聘岗位
              </h3>
              <div className="space-y-4">
                {similarJobs.map((simJob) => (
                  <Link
                    key={simJob.id}
                    to={`/jobs/${simJob.id}`}
                    className="block p-3.5 rounded-2xl bg-background-50 hover:bg-primary-50/50 transition-colors border border-transparent hover:border-primary-200 group"
                  >
                    <h4 className="font-semibold text-sm text-foreground-900 group-hover:text-primary-600 line-clamp-1 mb-1">
                      {simJob.title}
                    </h4>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-red-500 font-medium">
                        {formatJobSalaryDisplay(simJob.salary_min, simJob.salary_max, simJob.salary_type, simJob.salary_period)}
                      </span>
                      <span className="text-foreground-400">{simJob.city_name || '全国'}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      }
      comments={
        job.id ? (
          <CommentSection
            targetType="JOB_POSTING"
            targetId={job.id}
            title="岗位咨询与讨论"
            isTargetAuthor={Boolean(
              isLoggedIn &&
              user &&
              ((job as any).publisher_account_id
                ? user.id === (job as any).publisher_account_id
                : (job as any).author_account_id
                  ? user.id === (job as any).author_account_id
                  : false)
            )}
          />
        ) : null
      }
      modals={
        <>
          {/* Apply Modal */}
          <ApplyModal
            show={showApplyModal}
            onClose={() => setShowApplyModal(false)}
            submissionMode={submissionMode}
            setSubmissionMode={setSubmissionMode}
            directSalary={directSalary}
            setDirectSalary={setDirectSalary}
            directResumeFileName={directResumeFileName}
            isUploadingResume={isUploadingResume}
            handleResumeFileUpload={handleResumeFileUpload}
            jobResumeDraft={jobResumeDraft}
            applySuccess={applySuccess}
            applyError={applyError}
            isSubmitting={isSubmitting}
            onConfirmSubmit={handleConfirmSubmit}
          />

          {/* 未填写求职资料提示 Modal */}
          <DraftPromptModal
            show={showDraftPromptModal}
            onClose={() => setShowDraftPromptModal(false)}
            onOpenDirectResume={() => {
              setShowDraftPromptModal(false);
              setSubmissionMode('DIRECT_RESUME');
              setShowApplyModal(true);
            }}
          />

          {/* Login Prompt Modal */}
          <LoginPromptModal
            show={showLoginPrompt}
            onClose={() => setShowLoginPrompt(false)}
          />

          {/* 企业环境与资质媒体预览 Modal */}
          <PortfolioPreviewModal
            open={previewMedia.open}
            title={previewMedia.title}
            intro={previewMedia.intro}
            isVideo={previewMedia.isVideo}
            mediaUrl={previewMedia.mediaUrl}
            onClose={() => setPreviewMedia((prev) => ({ ...prev, open: false }))}
          />
        </>
      }
    >
      {/* 岗位描述 */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-background-200/80 shadow-sm">
        <h2 className="text-lg font-bold text-foreground-950 mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
          <i className="ri-file-text-line text-primary-500"></i>
          岗位描述
        </h2>
        <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed whitespace-pre-line">
          {job.job_description || '暂无岗位具体描述。'}
        </div>
      </div>

      {/* 岗位要求 */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-background-200/80 shadow-sm">
        <h2 className="text-lg font-bold text-foreground-950 mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
          <i className="ri-task-line text-primary-500"></i>
          岗位要求
        </h2>
        <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed whitespace-pre-line">
          {job.job_requirements || '暂无详细岗位要求。'}
        </div>
      </div>

      {/* 技能要求 */}
      {job.skill_requirements && (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-background-200/80 shadow-sm">
          <h2 className="text-lg font-bold text-foreground-950 mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
            <i className="ri-tools-line text-primary-500"></i>
            技能要求
          </h2>
          <div className="flex flex-wrap gap-2">
            {job.skill_requirements
              .split('|')
              .map((skill) => skill.trim())
              .filter(Boolean)
              .map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-xl text-sm font-medium border border-primary-100/60 flex items-center gap-1.5"
                >
                  <i className="ri-checkbox-circle-fill text-primary-400 text-xs"></i>
                  {skill}
                </span>
              ))}
          </div>
        </div>
      )}

      {/* 福利待遇 */}
      {job.benefits && (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-background-200/80 shadow-sm">
          <h2 className="text-lg font-bold text-foreground-950 mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
            <i className="ri-gift-line text-primary-500"></i>
            相关福利
          </h2>
          <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed whitespace-pre-line">
            {job.benefits}
          </div>
        </div>
      )}

      {/* 企业展示图片与资质预览 */}
      {job.enterprise_images && job.enterprise_images.length > 0 && (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-background-200/80 shadow-sm">
          <h2 className="text-lg font-bold text-foreground-950 mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
            <i className="ri-image-line text-primary-500"></i>
            企业环境与资质
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {job.enterprise_images.map((img, idx) => (
              <div
                key={idx}
                onClick={() => handlePreviewImage(img)}
                className={`relative rounded-2xl overflow-hidden min-h-[140px] max-h-[220px] bg-gray-100/80 border border-gray-200/80 group transition-all duration-300 flex items-center justify-center ${img.access_url ? 'cursor-pointer hover:shadow-md hover:border-primary-300' : 'cursor-not-allowed'
                  }`}
              >
                {img.access_url ? (
                  <>
                    <img
                      src={img.access_url}
                      alt={img.display_name || img.file_name || '企业环境资质图'}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center text-white p-3 text-center">
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mb-1 text-lg">
                        <i className="ri-search-2-line"></i>
                      </div>
                      <span className="text-xs font-medium truncate max-w-full">
                        {img.display_name || img.file_name || '点击大图预览'}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-3 text-xs text-foreground-400 text-center">
                    <i className="ri-image-line text-2xl text-gray-300 mb-1"></i>
                    {img.display_name || '无图片预览'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
}
